
import { Alert, Col, Row, Space } from 'antd';
import useSession from 'Hooks/session.hook';
import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { LocalCartAtom } from 'Recoil/order.atom';
import { InvalidVoucherStatusType } from '../purchase-list/purchase-list';
import { isNil, isEmpty, flatten, forEach, groupBy, map } from 'lodash';
import { fetchProductData } from 'Services/inventory.services';
import { encode } from 'Helpers/enc';
import { generateCode } from 'Helpers/generate-code';
import { createRePaymentObject, CreateRePaymentObjectPayload } from 'Services/payment.services';
import { BasicModal, Button, SkeletonLoader } from '@life-line-lab/components-react';
import { toaster } from 'Helpers/toaster';
import './payment-list.scss';
import { CancelOrderModal } from './modals/cancel-order-modal';
import { ProceedPaymentModal } from './modals/proceed-payment-modal';
import { DiscountVoucherInvalidModal } from './modals/discount-voucher-invalid-modal';

interface PaymentListProps {
  status: any;
  cancelOrder: (cancelObj: any) => Promise<void>;
  paymentsDataList: any[];
}

const PaymentList = ({ status, cancelOrder, paymentsDataList }: PaymentListProps) => {
  const { t } = useTranslation()
  const history = useHistory();
  const [localCartState, setLocalCartState] = useRecoilState<any>(LocalCartAtom);
  const { profile } = useSession();

  const [cancelPopup, setCancelPopup] = useState<boolean>(false);
  const [payNowPopup, setPayNowPopup] = useState<boolean>(false);
  const [invalidVoucherPopup, setInvalidVoucherPopup] = useState<boolean>(false);
  const [appliedInvalidVouchers, setAppliedInvalidVouchers] = useState<any[]>([])
  const [appliedInvalidVoucherStatus, setAppliedInvalidVoucherStatus] = useState<InvalidVoucherStatusType>('EXPIRED')

  const [cancelOrderObjList, setCancelOrderObjList] = useState<any[]>([])
  const [payNowOrderObjList, setPayNowOrderObjList] = useState<any[]>([])
  const [payNowPayment, setPayNowPayment] = useState<any>(undefined);

  const onCloseModal = () => {
    setCancelPopup(false)
    setInvalidVoucherPopup(false)
    setPayNowPopup(false)
  }

  const onProductClick = (inventory) => {
    const inventoryID = encode(inventory._id);
    history.push('/products/' + inventoryID);
  }

  const onCancel = (objList: { orderCode: string, [key: string]: any }[]) => {
    setCancelPopup(true);
    setCancelOrderObjList(objList);
  }

  const confirmCancel = async () => {
    setCancelPopup(false)

    for (const cancelOrderObj of cancelOrderObjList) {
      const cancelObj = {
        orderID: cancelOrderObj._id,
        newStatus: 'CANCELLED'
      }
      await cancelOrder(cancelObj);
    }
  }

  useEffect(() => {
    if (!profile) {
      history.push('/login')
    }
  }, [profile])

  const handleCheckoutPreviousOrderWithPendingPayment = async () => {
    const itemsToAdd: any[] = flatten(payNowOrderObjList.map(orderObj => orderObj.items));
    const cartList: any[] = [];
    let totalWeight = 0;

    for (const item of itemsToAdd) {
      const productData = await fetchProductData({ inventoryID: item.inventoryID });

      // * refer product-details.tsx
      const productType = productData.product.productType;
      let productUOMs: any = {};
      if (!isEmpty(productData.product?.baseUOM) || !isEmpty(productData.product?.stockUOMs)) {
        productUOMs[productData.product?.baseUOM.uom] = productData.product?.baseUOM.weight?.value || "0"
        forEach(productData.product?.stockUOMs, uomObj => {
          productUOMs[uomObj.uom] = uomObj.weight?.value || "0"
        })
      }
      let itemWeight = item?.weight?.value ?? item?.weight ??
        productUOMs[item.uom];

      const cartDetails = {
        cartID: generateCode('CRT-'),
        productData: productData,
        selectedVariant: productData.stockVariants.find(s => s.variantCode === item.variantCode),
        quantity: item.quantity,
        weight: productType != 'VOUCHER' && productType !== 'NO_SHIPPING' ? parseFloat(itemWeight) * parseFloat(item.quantity) : 0
      }

      if (productType !== 'VOUCHER' && productType !== 'NO_SHIPPING') {
        totalWeight += parseFloat(itemWeight) * parseFloat(item.quantity)
      }

      cartList.push(cartDetails)
    }

    const supplierGroup = groupBy(cartList, cartObj => {
      return cartObj.productData.orgID;
    })

    const shippingCostItems = map(supplierGroup, (data, id) => {
      let weight = 0;
      data.forEach((item) => {
        weight += item.weight // do not multiply by quantity, because it is already multiplied when creating cartDetails and putting into cartList
      })
      return {
        supplierID: id,
        state: profile.primaryAddress.state,
        weight: weight
      }
    })

    const shippingCostObj = {
      orders: shippingCostItems,
    }

    setLocalCartState({
      ...localCartState, list: [...cartList], fromBuyNow: true, shippingCostObj,
    });
    history.push('/checkout', {
      isCancelPreviousOrder: true,
      cancelOrderObjList: payNowOrderObjList,
    });
  }

  const handlePayNow = async (orderObjList: any[]) => {
    const orderIdList = orderObjList.map(o => o._id);
    let paymentType = undefined;
    if (process.env.GATEWAY_TYPE === 'REVPAY') {
      // get the payment 'code' from RevPay options
      // * refer product-proceed.tsx
      history.push('/proceed', {
        isRepayment: true,
        orderObjList: orderObjList,
        orderIdList,
        paymentDetails: payNowPayment,
      });
      return;
    }

    // * if is kiplepay
    // * Call the repayment API
    const paymentObj: CreateRePaymentObjectPayload = {
      paymentID: payNowPayment._id,
      orderIDs: !isEmpty(orderIdList) ? orderIdList : null,
      shipName: profile.fullName,
      email: profile.primaryEmail,
      phone: profile.primaryPhone.fullNumber,
      appliedPlatformVouchers: localCartState?.appliedPlatformVouchers,
      paymentBetween: "USER_PLATFORM",
      revpay_payment_code: !isNil(paymentType) ? parseInt(paymentType) : undefined
    };

    const result: { error?: any, [key: string]: any } = await createRePaymentObject(paymentObj);

    if (result.error) {
      // * if is actual ERROR, display error message, then RETURN
      const errorInfo = result.error.info;
      if (errorInfo && errorInfo?.timeLimitExceeded) {
        toaster('error', t('toaster.repaymentTimeLimitExceeded'));
        return;
      }
      if (!errorInfo || !errorInfo?.voucherCode) {
        toaster('error', t('toaster.failedValidateOrderForPayment'));
        return;
      }

      // * if is invalid voucher, display accordingly
      let voucherErrorInfo: { voucherCode: string, voucherStatus: InvalidVoucherStatusType } = errorInfo;
      const invalidVoucher = voucherErrorInfo.voucherCode;
      const invalidVoucherList = [invalidVoucher]
      setAppliedInvalidVouchers(invalidVoucherList)
      setAppliedInvalidVoucherStatus(voucherErrorInfo.voucherStatus)
      setInvalidVoucherPopup(true)
      return;
    }

    const orderIDs = !isEmpty(orderIdList) ? orderIdList : null;
    const encodedOrderIDs = encode(JSON.stringify(orderIDs));

    history.push(`/payment/submit?ref=${encodedOrderIDs}`, {
      isRepayment: true,
      data: result,
    });
  }

  return (
    <div className="product payment-list">

      {status.fetch == 'loading' &&
        <>
          <SkeletonLoader type="default" />
          <SkeletonLoader type="default" />
          <SkeletonLoader type="default" />
        </>
      }

      <CancelOrderModal 
        cancelPopup={cancelPopup} 
        confirmCancel={confirmCancel} 
        onCloseModal={onCloseModal} 
        />
      <ProceedPaymentModal 
        payNowPopup={payNowPopup} 
        onCloseModal={onCloseModal} 
        confirmPay={() => handlePayNow(payNowOrderObjList)} 
        />
      <DiscountVoucherInvalidModal 
        invalidVoucherPopup={invalidVoucherPopup} 
        onCloseModal={onCloseModal} 
        appliedInvalidVoucherStatus={appliedInvalidVoucherStatus}
        appliedInvalidVouchers={appliedInvalidVouchers} 
        handleCheckoutPreviousOrderWithPendingPayment={handleCheckoutPreviousOrderWithPendingPayment}
      />

      <Row gutter={16}>
        <Col lg={24} md={24} sm={24} xs={24}>
          {paymentsDataList.length && status.fetch !== 'loading' ?
            <Alert
              type="info"
              showIcon
              message={(
                <Fragment>
                  <span>{t('txt.orderQueries')}</span>
                  <Link
                    style={{ marginRight: '3px', marginLeft: '3px', color: '#B42C30' }}
                    to="/support"
                  >
                    {t("txt.contactSupport")}
                  </Link>
                  <span> {t('txt.forAssistance')}</span>
                </Fragment>
              )}
            />
            : ''}
        </Col>
        <Col lg={24} md={24} sm={24} xs={24}>
          {paymentsDataList.length && status.fetch !== 'loading' ? paymentsDataList.map((payment, i) => {
            return (
              <div key={i} className="payment-wrapper">
                <p className="payment-title">Payment ID. {payment._key}</p>
                {
                  payment?.orders && payment.orders.map((o, i) => {
                    return (
                      <div className="shopping-details my-20">
                        <Col lg={24} md={24} sm={24} xs={24}>

                          <div className="cart-table">
                            <div>
                              <div className="purchase-section1">
                                <Row gutter={16} className="rrow">
                                  <Col lg={1} md={1} sm={1} xs={0} className="ccol"></Col>

                                  <Col lg={4} md={4} sm={8} xs={12} className="ccol flex-align-center">
                                    <div className="title">#{o.orderCode}</div>
                                  </Col>

                                  {o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING' ? <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                                    <div className="title">{t('label.shippingTo')}</div>
                                    <div className="val">
                                      <strong>{o.receiver?.fullName}</strong>
                                    </div>
                                  </Col> : null}
                                  <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                                    <div className="title">{t('label.orderPaymentStatus')}</div>
                                    <h4 className="pay-sts color-secondary">
                                      <strong>
                                        {payment.status && payment.status != 'NOT_FOUND' ? payment.status : t('txt.UNPAID')}
                                      </strong>
                                    </h4>
                                  </Col>
                                  {o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING' && <>
                                    <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                                      <div className="title">{t('label.latestDeliveryStatus')}</div>
                                      {o.payment_data?.status == 'PAID' ? <>
                                        {o?.status != 'CANCELLED' && o?.status != 'REJECTED' && o?.status != 'REFUNDED' ? <h4 className="val color-secondary"><strong>{o?.latestDispatchUpdate?.status || t('txt.orderProcessing')}</strong></h4> : '-'}
                                        {o?.latestDispatchUpdate?.timestamp &&
                                          <div className="font-12">
                                            <strong>{moment(o?.latestDispatchUpdate?.timestamp).format('DD-MM-YYYY hh:mm a')}</strong>
                                          </div>
                                        }
                                      </> : <h4>-</h4>}
                                    </Col>
                                  </>}
                                </Row>

                                <Row gutter={16} style={{ borderBottom: '1px solid #ddd', borderRadius: '6px' }}>
                                  <Col lg={20} md={20} sm={20} xs={24} className="ccol">
                                    {o.items.map((record, j) => {
                                      let itemImage = record.product.product.images?.[0]?.file_data?.file_url || '/assets/images/product-placeholder.png';
                                      const variantCode = record.variantCode;
                                      if (variantCode && record.product.product?.images?.length) {
                                        const matchedImageLink = record.product.product?.images.find(elem => elem.variantCode === variantCode);
                                        if (matchedImageLink) {
                                          itemImage = matchedImageLink.file_data?.file_url || itemImage;
                                        }
                                      }

                                      return (
                                        <Row gutter={16} className={"image-section " + (o.status == 'OPEN' ? '' : '')}>
                                          <Col lg={1} md={1} sm={1} xs={0} className="ccol"></Col>
                                          <Col lg={3} md={3} sm={3} xs={6} className="ccol flex-align-center">
                                            <img
                                              alt={t('alt.logo')}
                                              src={itemImage}
                                              style={{ width: '100%' }}
                                              onError={({ currentTarget }) => {
                                                currentTarget.onerror = null; // prevents looping
                                                currentTarget.src = '/assets/images/product-placeholder.png';
                                              }}
                                            /> &nbsp;
                                          </Col>
                                          <Col lg={12} md={12} sm={12} xs={12} className="ccol info">
                                            <div onClick={() => onProductClick(record.product)} style={{ textTransform: 'capitalize', fontSize: '16px' }}> <strong>{record.product.product.name}</strong></div>
                                            <h5 className="color-secondary"><strong>({record.variant}) x {record.quantity}</strong></h5>
                                            <h4 className="color-secondary"><strong>RM {parseFloat(record.unitPrice).toFixed(2)}</strong></h4>
                                          </Col>
                                          {record?.vouchers ?
                                            <Col lg={6} md={6} sm={24} xs={24} className="ccol m-auto">
                                              {record?.vouchers?.map((voucher, j) => {
                                                return (
                                                  <Col lg={24} md={24} sm={24} xs={24} className="ccol">
                                                    <div className="coupon-voucher">
                                                      <div className="inner">
                                                        <div className="left-circle"></div>
                                                        <h5 className="code"><strong>{voucher.code}</strong></h5>

                                                        {voucher.status == 'ACTIVE' ? <h5 className="validity"><strong>Valid till {moment(voucher.expiresAt).format('DD-MM-YYYY')}</strong></h5>
                                                          :
                                                          <h5 className={"validity -" + voucher.status}><strong>{voucher.status}</strong></h5>}

                                                        <div className="right-circle"></div>
                                                      </div>
                                                    </div>
                                                  </Col>
                                                )
                                              })}
                                            </Col> : null}
                                        </Row>
                                      )
                                    })}
                                  </Col>
                                </Row>
                                <div>

                                  <Row gutter={16} className="order-total">
                                    <Col span={12} xs={12}> {t('txt.soldby')} : {o.supplier?.name} </Col>
                                    <Col span={12} xs={12} className="text-end"> {t('txt.orderTotal')} : RM{parseFloat(o.paymentDetails.total).toFixed(2)}</Col></Row>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </div>
                    )
                  })
                }
                <Row gutter={16} style={{ borderBottom: '1px solid #ddd', borderRadius: '6px' }}>
                  <Col lg={24} md={24} sm={24} xs={24} className="ccol">
                    <div className="price-list">
                      {t('label.paymentTotal')} : RM{parseFloat(payment.total).toFixed(2)}
                    </div>
                  </Col>

                  <Col lg={20} md={20} sm={20} xs={24} className="ccol">
                    {/* nothing here */}
                  </Col>
                  <Col lg={4} md={4} sm={4} xs={24} className="canceBtn">
                    <Row>
                      <Col lg={24} md={24} sm={24} xs={24} className="py-5 px-5">
                        <Button
                          type="primary"
                          style={{ width: '100%' }}
                          label={t('action.payNow')}
                          size="large"
                          onClick={() => {
                            // set the order/cart data
                            setPayNowPayment(payment)
                            setPayNowOrderObjList(payment.orders)
                            setPayNowPopup(true)
                          }}
                        />
                      </Col>
                      <Col lg={24} md={24} sm={24} xs={24} className="py-5 px-5">
                        <Button
                          type="secondary"
                          style={{ width: '100%' }}
                          label={t('action.cancelOrder')}
                          size="large"
                          onClick={() => {
                            onCancel(payment.orders)
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>


              </div>
            )
          }) :

            status.fetch == 'success' && 
            <Row>
              <div className="my-10 m-auto justify-center">
                <div>
                  <img
                    className="mx-25"
                    alt={t('alt.logo')}
                    src='/assets/images/empty-order.png'
                    width={150}
                  />
                  <h3 className="color-primary"><strong>{t('txt.noPurchaseHistoryFound')}</strong></h3>
                </div>
              </div>
            </Row>
          }
        </Col>
      </Row>
    </div >
  )
}

export default PaymentList;